import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import SEO from '../../Components/SEO/SEO';
import BlogArt from './BlogArt';
import BlogShot, { shotAlt, shotBySlug } from './BlogShot';
import styles from './blog.module.css';
import { postBySlug, relatedPosts } from '../../blog/posts';
import { BLOG_POST_META } from '../../seo/pageMeta';
import { SITE_ORIGIN } from '../../utils/locale';

/**
 * One post.
 *
 * The body is DATA, not markup in a translation string: `blog.posts.<slug>.body`
 * is an array of `{type, text}` blocks, so the same post renders identically in
 * three languages and a translator never has to reproduce HTML. It also means
 * the words are in the DOM on the first paint — which is the whole reason this
 * section exists, after a feature page spent months marking up answers in
 * JSON-LD that were nowhere on the page.
 */
export default function BlogPost({ slug }) {
  const { t, i18n } = useTranslation();
  const post = postBySlug(slug);
  if (!post) return null;

  const base = `blog.posts.${slug}`;
  const title = t(`${base}.title`);
  const excerpt = t(`${base}.excerpt`);
  const body = t(`${base}.body`, { returnObjects: true }) || [];
  const blocks = Array.isArray(body) ? body : [];
  const related = relatedPosts(slug);
  const meta = BLOG_POST_META[slug] || {};

  /* ~200 words a minute, from the words actually rendered. Honest, and it
   * changes with the translation rather than being a number typed once. */
  const textOf = (b) => {
    if (b.type === 'list') return (b.items || []).join(' ');
    /* A shot carries no prose of its own — its words are the manifest's
     * caption. Counting `undefined` here used to add a phantom word per
     * image, which is small but makes the reading time a lie. */
    if (b.type === 'shot') return shotAlt(b.slug, i18n.language);
    return b.text || '';
  };
  const words = blocks.reduce(
    (n, b) => n + String(textOf(b)).split(/\s+/).filter(Boolean).length,
    0,
  );
  const minutes = Math.max(2, Math.round(words / 200));

  const url = `${SITE_ORIGIN}${i18n.language === 'en' ? '' : `/${i18n.language}`}/blog/${slug}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: title,
      description: excerpt,
      datePublished: post.date,
      dateModified: post.date,
      inLanguage: i18n.language,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      author: { '@type': 'Organization', name: 'Stylon', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'Stylon', url: SITE_ORIGIN },
      articleSection: t(`blog.categories.${post.category}.name`),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Stylon', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: t('blog.index.title'), item: `${SITE_ORIGIN}/blog` },
        { '@type': 'ListItem', position: 3, name: title },
      ],
    },
  ];

  /* THE PICTURES, DECLARED. Each shot in the body is a real screenshot of the
   * product in the reader's own language; stating it as an ImageObject is what
   * lets an image search or an answer engine attribute it, and it costs
   * nothing because the caption is already written and translated. */
  for (const block of blocks) {
    if (block.type !== 'shot' || !shotBySlug(block.slug)) continue;
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      contentUrl: `${SITE_ORIGIN}/shots/${i18n.language === 'he' || i18n.language === 'ar' ? i18n.language : 'en'}/${block.slug}.webp`,
      caption: shotAlt(block.slug, i18n.language),
      representativeOfPage: false,
    });
  }

  return (
    <>
      <SEO title={meta.title} description={meta.description} canonical={`/blog/${slug}`} jsonLd={jsonLd} />
      <Navbar />
      <article className={styles.wrap}>
        <Link to="/blog" className={styles.back}>&larr; {t('blog.index.backToBlog')}</Link>
        <p className={styles.kicker}>{t(`blog.categories.${post.category}.name`)}</p>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.lede}>{excerpt}</p>
        <div className={styles.meta}>
          <span>{t('blog.index.readingTime', { minutes })}</span>
          <span>{t('blog.index.published', { date: post.date })}</span>
        </div>
        <div className={styles.hero}><BlogArt slug={slug} /></div>

        <div className={styles.body}>
          {blocks.map((block, i) => {
            if (block.type === 'h') return <h2 key={i}>{block.text}</h2>;
            if (block.type === 'list') {
              return <ul key={i}>{(block.items || []).map((item, j) => <li key={j}>{item}</li>)}</ul>;
            }
            if (block.type === 'shot') return <BlogShot key={i} slug={block.slug} />;
            return <p key={i}>{block.text}</p>;
          })}
        </div>

        <div className={styles.cta}><Link to="/features">{t('blog.index.cta')} &rarr;</Link></div>

        {related.length > 0 && (
          <section className={styles.related}>
            <h2 className={styles.relatedTitle}>{t('blog.index.related')}</h2>
            <div className={styles.grid}>
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className={styles.card}>
                  <BlogArt slug={r.slug} />
                  <div className={styles.cardBody}>
                    <p className={styles.cardCat}>{t(`blog.categories.${r.category}.name`)}</p>
                    <h3 className={styles.cardTitle}>{t(`blog.posts.${r.slug}.title`)}</h3>
                    <p className={styles.cardExcerpt}>{t(`blog.posts.${r.slug}.excerpt`)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
      <Footer />
    </>
  );
}
